import _ from 'lodash';
import { TranslateResult } from 'vue-i18n';

import { IFlexibleLayoutItems } from '@onereach/grid-layout';
import { ISerializedFlexibleLayoutItem } from '@/modules/layout-builder/types';
import {
  GL_COLUMNS,
  GL_ROWS,
  FL_COLUMNS,
  FL_ROWS,
} from '@/modules/layout-builder/constants';
import {
  DEFAULT_WORKSPACE,
  DEFAULT_LAYOUT,
  DEFAULT_LAYOUT_KEY,
  LAYOUT_VERSION,
} from './constants';
import {
  ILayoutWorkspace,
  LayoutWorkspaceList,
  ILayout,
  ILayoutSettings,
} from './types';

function migrateToUiLayoutV2(
  items: ISerializedFlexibleLayoutItem[],
  version?: string,
) {
  if (version && (parseFloat(version) === 2)) {
    return items;
  }

  return items.map(item => ({
    ...item,
    row: Math.round(item.row / GL_ROWS * FL_ROWS),
    column: Math.round(item.column / GL_COLUMNS * FL_COLUMNS),
    width: Math.round(item.width / GL_COLUMNS * FL_COLUMNS),
    height: Math.round(item.height / GL_ROWS * FL_ROWS),
  }));
}

export function isUiLayoutV3(settingsData: ILayoutSettings) {
  const { workspaceList } = settingsData;
  return !_.isEmpty(workspaceList);
}

export function areLayoutsEqual(a: ILayout, b: ILayout) {
  const OMIT_PROPS = ['createdAt'];
  return _.isEqual(
    _.omit(a, OMIT_PROPS),
    _.omit(b, OMIT_PROPS),
  );
}

export function areWorkspaceListsEqual(a: LayoutWorkspaceList, b: LayoutWorkspaceList) {
  const LAYOUT_OMIT_PROPS = ['createdAt'];
  const listA = _.cloneDeep(a);
  const listB = _.cloneDeep(b);
  const patchWorkspace = (workspace: ILayoutWorkspace, index: number) => {
    workspace.order = index;
    const { layouts } = workspace;
    Object.keys(layouts).forEach(layoutKey => {
      const layout = layouts[layoutKey];
      (layouts[layoutKey] as any) = _.omit(layout, LAYOUT_OMIT_PROPS);
    });
  };
  listA.forEach(patchWorkspace);
  listB.forEach(patchWorkspace);
  return _.isEqual(listA, listB);
}

function orderWorkspaceList(settingsData: ILayoutSettings) {
  const sortedWorkspaceList = settingsData.workspaceList.sort((a, b) => a.order - b.order);
  Object.assign(settingsData, { workspaceList: sortedWorkspaceList });
  return settingsData;
}

export function migrateToUiLayoutV3(
  data?: ILayoutSettings,
) {
  const settingsData = ({ ...data } as unknown) as ILayoutSettings;
  const { version, items } = settingsData;

  if (isUiLayoutV3(settingsData)) {
    return orderWorkspaceList(settingsData);
  }

  let workspaceList: LayoutWorkspaceList;

  const defaultWorkspace = {
    ...DEFAULT_WORKSPACE,
  };

  if (items) {
    const itemsV2 = migrateToUiLayoutV2(items, version);
    Object.assign(settingsData, { items: itemsV2 });

    workspaceList = [
      {
        ...defaultWorkspace,
        components: itemsV2.map(item => item.key),
        layouts: {
          [DEFAULT_LAYOUT_KEY]: {
            ...DEFAULT_LAYOUT,
            items: _.keyBy(itemsV2, 'key'),
          },
        },
      },
    ];
  } else {
    workspaceList = [defaultWorkspace];
  }

  Object.assign(settingsData, {
    version: LAYOUT_VERSION,
    workspaceList,
  });

  return orderWorkspaceList(settingsData);
}

export function translateWorkspaceList(
  workspaceList: LayoutWorkspaceList,
  translate?: (s: string) => TranslateResult,
) {
  const $t = translate || ((s: string) => s);

  return workspaceList.map(workspace => {
    return {
      ...workspace,
      name: $t(workspace.name) as string,
      layouts: Object.values(workspace.layouts)
        .reduce((result, layout) => {
          result[layout.key] = {
            ...layout,
            name: $t(layout.name) as string,
            items: Object.values(layout.items)
              .reduce((result, item) => {
                result[item.key] = {
                  ...item,
                  label: $t(item.label) as string,
                };
                return result;
              }, {} as IFlexibleLayoutItems),
          };
          return result;
        }, {} as Record<string, ILayout>),
    };
  });
}
